import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, Minus, ShoppingBag, WifiOff, RefreshCw, Search, ChevronRight, Sparkles, X } from "lucide-react";
import GuestLayout from "@/components/GuestLayout";
import api, { getMenuItems, getSettings, getCategorySettings } from "@/lib/api";
import { getPropertyId, getPropertySlug, addToCart, getCart, updateCartQuantity } from "@/lib/store";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";

// Standard FSSAI Indian Dietary Indicator (Green Circle in Square / Red-Brown Triangle in Square)
const DietaryBadge = ({ isVeg }) => {
  if (isVeg) {
    return (
      <span className="inline-flex items-center justify-center w-4 h-4 border-[1.5px] border-emerald-600 rounded-[3px] p-[1.5px] bg-white flex-shrink-0 shadow-sm" title="Pure Vegetarian">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-600"></span>
      </span>
    );
  }
  return (
    <span className="inline-flex items-center justify-center w-4 h-4 border-[1.5px] border-[#b91c1c] rounded-[3px] p-[1.5px] bg-white flex-shrink-0 shadow-sm" title="Non-Vegetarian">
      <span className="w-0 h-0 border-l-[3.5px] border-l-transparent border-r-[3.5px] border-r-transparent border-b-[6px] border-b-[#b91c1c]"></span>
    </span>
  );
};

// Subtle Destination Cultural Silhouette Divider
const DestinationSilhouette = ({ isVaranasi }) => {
  if (isVaranasi) {
    // Varanasi Ghats, Temple Shikhars & River Steps Silhouette
    return (
      <div className="absolute bottom-0 left-0 right-0 w-full overflow-hidden leading-none z-10 pointer-events-none opacity-40">
        <svg viewBox="0 0 1200 60" preserveAspectRatio="none" className="w-full h-8 text-[#FBF9F6] fill-current">
          <path d="M0,60 L0,45 Q40,40 70,48 Q100,55 130,42 L130,30 L135,25 L140,30 L140,42 Q170,45 200,38 L200,20 L205,10 L210,20 L210,38 Q240,40 270,45 L270,35 L275,30 L280,35 L280,45 Q310,48 340,40 L340,15 L345,5 L350,15 L350,40 Q400,45 450,42 L450,28 L455,22 L460,28 L460,42 Q500,45 550,38 L550,18 L555,8 L560,18 L560,38 Q600,42 650,40 L650,25 L655,18 L660,25 L660,40 Q700,42 750,36 L750,15 L755,5 L760,15 L760,36 Q800,45 850,40 L850,28 L855,20 L860,28 L860,40 Q900,45 950,38 L950,16 L955,6 L960,16 L960,38 Q1000,42 1050,45 L1050,30 L1055,24 L1060,30 L1060,45 Q1120,50 1200,40 L1200,60 Z" />
        </svg>
      </div>
    );
  }
  // Darjeeling Misty Mountains & Pine Silhouette
  return (
    <div className="absolute bottom-0 left-0 right-0 w-full overflow-hidden leading-none z-10 pointer-events-none opacity-40">
      <svg viewBox="0 0 1200 60" preserveAspectRatio="none" className="w-full h-8 text-[#FBF9F6] fill-current">
        <path d="M0,60 L0,48 L80,38 L160,50 L240,32 L300,18 L360,42 L440,28 L520,46 L600,20 L660,35 L720,15 L800,40 L880,25 L940,42 L1020,30 L1100,45 L1200,35 L1200,60 Z" />
      </svg>
    </div>
  );
};

export default function FoodMenu() {
  const navigate = useNavigate();
  const propertyId = getPropertyId();
  const propertySlug = getPropertySlug();
  const isVaranasi = !propertyId.toLowerCase().includes("darjeeling");

  const [menuItems, setMenuItems] = useState([]);
  const [propertyInfo, setPropertyInfo] = useState(null);
  const [settings, setSettings] = useState(null);
  const [categorySettings, setCategorySettings] = useState([]);
  const [isOffline, setIsOffline] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [vegOnly, setVegOnly] = useState(false);
  const [cart, setCart] = useState([]);
  const [networkError, setNetworkError] = useState(false);

  const fetchMenu = useCallback(async () => {
    setLoading(true);
    setNetworkError(false);
    try {
      const [menuData, infoRes, settingsData, catData] = await Promise.all([
        getMenuItems(propertyId),
        api.get(`/property-info/${propertyId}?t=${Date.now()}`).catch(() => ({ data: null })),
        getSettings(propertyId).catch(() => null),
        getCategorySettings(propertyId).catch(() => [])
      ]);
      setMenuItems(Array.isArray(menuData) ? menuData : []);
      if (infoRes?.data) setPropertyInfo(infoRes.data);
      if (settingsData) {
        setSettings(settingsData);
        if (settingsData.food_enabled === false) {
          setIsOffline(true);
        } else {
          setIsOffline(false);
        }
      }
      if (Array.isArray(catData)) {
        setCategorySettings(catData);
        // Automatically check if all categories are currently outside their daily time slots
        const anyActiveCategory = catData.some(c => c.is_enabled !== false && c.is_active);
        if (catData.length > 0 && !anyActiveCategory) {
          setIsOffline(true);
        }
      }
    } catch (error) {
      console.error("Error fetching menu:", error);
      if (error.isNetworkError) {
        setNetworkError(true);
      }
    } finally {
      setLoading(false);
    }
  }, [propertyId]);

  useEffect(() => {
    fetchMenu();
    setCart(getCart(propertyId));

    const syncCart = () => setCart(getCart(propertyId));
    window.addEventListener("cartUpdated", syncCart);
    window.addEventListener("storage", syncCart);
    return () => {
      window.removeEventListener("cartUpdated", syncCart);
      window.removeEventListener("storage", syncCart);
    };
  }, [fetchMenu, propertyId]);

  // Unique Non-empty Categories
  const categories = useMemo(() => {
    const cats = new Set();
    menuItems.forEach((item) => {
      if (item.category) cats.add(item.category);
      if (Array.isArray(item.categories)) item.categories.forEach((c) => c && cats.add(c));
    });
    return ["all", ...cats];
  }, [menuItems]);

  // Returns all categories an item belongs to (primary + multi)
  const getItemCategories = (item) => {
    const all = new Set();
    if (item.category) all.add(item.category);
    if (Array.isArray(item.categories)) item.categories.forEach((c) => c && all.add(c));
    return all;
  };

  // Filtered Menu Items based on category, search query, and veg-only filter
  const filteredItems = useMemo(() => {
    return menuItems.filter((item) => {
      // Category filter — check primary AND multi-category list
      if (activeCategory !== "all" && !getItemCategories(item).has(activeCategory)) return false;
      // Veg-only filter (respects default true when undefined)
      if (vegOnly && item.is_vegetarian === false) return false;
      // Search query filter
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        const nameMatch = (item.name || "").toLowerCase().includes(query);
        const descMatch = (item.description || "").toLowerCase().includes(query);
        const catMatch = [...getItemCategories(item)].some((c) => c.toLowerCase().includes(query));
        if (!nameMatch && !descMatch && !catMatch) return false;
      }
      return true;
    });
  }, [menuItems, activeCategory, vegOnly, searchQuery]);

  const getItemQuantity = (itemId) => {
    const cartItem = cart.find((i) => i.id === itemId);
    return cartItem ? cartItem.quantity : 0;
  };

  const handleAddToCart = (item) => {
    const newCart = addToCart(item, propertyId);
    setCart(newCart);
    window.dispatchEvent(new Event("cartUpdated"));
    toast.success(`${item.name} added to order`);
  };

  const handleUpdateQuantity = (itemId, quantity) => {
    const newCart = updateCartQuantity(itemId, quantity, propertyId);
    setCart(newCart);
    window.dispatchEvent(new Event("cartUpdated"));
  };

  // Cart summary for floating bottom action bar
  const cartTotalQuantity = useMemo(() => cart.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0), [cart]);
  const cartTotalPrice = useMemo(() => cart.reduce((sum, item) => sum + ((Number(item.price) || 0) * (Number(item.quantity) || 0)), 0), [cart]);

  // Fallback cafe banner image
  const defaultHeader = isVaranasi 
    ? "https://images.unsplash.com/photo-1514933651103-005eec06c04b?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200"
    : "https://images.unsplash.com/photo-1544025162-d76694265947?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200";

  return (
    <GuestLayout>
      <div className="min-h-screen bg-[#FBF9F6] pb-44" data-testid="food-menu">
        {isOffline && (
          <div className="bg-[#264653] text-[#FBF9F6] px-4 py-4 flex flex-col items-center justify-center text-center">
            <span className="font-semibold text-lg mb-1">Our kitchen is currently closed.</span>
            <span className="text-sm opacity-90">Please reach out to the front desk—our team would be happy to assist you with pre-ordering!</span>
          </div>
        )}
        {/* Network Error Banner */}
        {networkError && (
          <div className="bg-red-500 text-white px-4 py-3 flex items-center justify-between" data-testid="network-error-banner">
            <div className="flex items-center gap-2">
              <WifiOff className="w-5 h-5" />
              <span className="font-medium text-sm">Poor Network Connection</span>
            </div>
            <Button
              size="sm"
              variant="ghost"
              className="text-white hover:bg-red-600 h-8"
              onClick={fetchMenu}
            >
              <RefreshCw className="w-3.5 h-3.5 mr-1" />
              Retry
            </Button>
          </div>
        )}

        {/* Destination-Aware Hero Header */}
        <div className="relative h-[28vh] min-h-[220px] overflow-hidden bg-[#264653]">
          <img
            src={propertyInfo?.food_header_image || defaultHeader}
            alt="Cafe & Dining"
            className="absolute inset-0 w-full h-full object-cover"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = defaultHeader;
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#264653] via-[#264653]/60 to-black/40" />

          {/* Cultural Skyline Silhouette along base of hero */}
          <DestinationSilhouette isVaranasi={isVaranasi} />

          {/* Floating Back Button */}
          <button
            onClick={() => navigate(`/${propertySlug}`)}
            className="absolute top-4 left-4 z-20 w-10 h-10 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center text-white hover:bg-white/30 transition-all border border-white/20 shadow-md active:scale-95"
            data-testid="back-button"
            title="Back to Dashboard"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>

          {/* Header Copy */}
          <div className="absolute inset-0 flex flex-col justify-end p-5 md:p-8 z-10">
            <div className="flex items-center gap-2 mb-1">
              <span className="px-2.5 py-0.5 rounded-full bg-[#FFC107] text-[#264653] text-[11px] font-bold uppercase tracking-wider shadow-sm">
                {settings?.property_name ? `${settings.property_name.split(',')[0]} Kitchen` : 'Rooftop Cafe'}
              </span>
              {settings?.property_location && (
                <span className="text-white/80 text-xs font-medium hidden sm:inline-block">
                  • {settings.property_location}
                </span>
              )}
            </div>
            <h1 
              className="text-2xl md:text-4xl text-white font-bold tracking-tight"
              style={{ fontFamily: "'Quicksand', sans-serif" }}
              data-testid="page-title"
            >
              Food & Refreshments
            </h1>
            <p className="text-white/75 text-xs md:text-sm mt-0.5 max-w-md">
              Freshly prepared comfort bites, local specialties, and steaming chai
            </p>
          </div>
        </div>

        {/* Search & Filter Bar */}
        <div className="px-4 md:px-8 mt-4">
          <div className="bg-white rounded-2xl p-2 md:p-3 shadow-sm border border-[#E8E4D9]/60 flex flex-col sm:flex-row gap-2.5 items-stretch sm:items-center justify-between">
            {/* Search Input */}
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search dishes, chai, snacks..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-9 py-2 rounded-xl text-sm bg-[#FBF9F6] border-none focus:outline-none focus:ring-2 focus:ring-[#2A9D8F]/20 text-[#264653]"
              />
              {searchQuery && (
                <button 
                  onClick={() => setSearchQuery("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            {/* Veg Only Toggle Pill */}
            <button
              onClick={() => setVegOnly(!vegOnly)}
              className={`flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-semibold border transition-all ${
                vegOnly 
                  ? "bg-emerald-50 border-emerald-500 text-emerald-800 shadow-sm" 
                  : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"
              }`}
            >
              <DietaryBadge isVeg={true} />
              <span>Veg Only</span>
            </button>
          </div>
        </div>

        {/* Category Pills Bar (Sticky) */}
        <div className="px-4 md:px-8 py-3 overflow-x-auto sticky top-0 z-20 bg-[#FBF9F6]/95 backdrop-blur-md">
          <div className="flex gap-2" data-testid="category-filters">
            {categories.map((category) => {
              const catSetting = categorySettings.find(c => c.category === category);
              const isInactive = catSetting && (catSetting.is_enabled === false || catSetting.is_active === false);
              return (
                <button
                  key={category}
                  onClick={() => setActiveCategory(category)}
                  className={`category-pill whitespace-nowrap capitalize text-xs md:text-sm font-semibold transition-all px-4 py-1.5 rounded-full flex items-center gap-1.5 ${
                    activeCategory === category 
                      ? "bg-[#2A9D8F] text-white shadow-sm" 
                      : "bg-white text-[#264653]/75 border border-[#E8E4D9] hover:border-[#2A9D8F]"
                  }`}
                  data-testid={`category-${category}`}
                >
                  <span>{category === "todays_special" ? "Today's Special" : category}</span>
                  {isInactive && (
                    <span className={`text-[10px] px-1.5 py-0.2 rounded-full font-bold ${
                      activeCategory === category ? "bg-white/20 text-white" : "bg-red-100 text-red-600"
                    }`}>
                      Closed
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Menu Items Grid */}
        <div className="px-4 md:px-8 mt-2">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="flex gap-3.5 p-4 bg-white rounded-2xl border border-[#E8E4D9]/60">
                  <Skeleton className="w-24 h-24 md:w-28 md:h-28 rounded-xl flex-shrink-0" />
                  <div className="flex-1 space-y-2 py-1">
                    <Skeleton className="h-5 w-3/4" />
                    <Skeleton className="h-3.5 w-full" />
                    <Skeleton className="h-5 w-16 mt-2" />
                  </div>
                </div>
              ))}
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-3xl border border-[#E8E4D9]/60 p-8 mt-2">
              <Sparkles className="w-12 h-12 text-[#FFC107] mx-auto mb-3" />
              <h3 className="font-bold text-[#264653] text-base" style={{ fontFamily: "'Quicksand', sans-serif" }}>
                No dishes found
              </h3>
              <p className="text-gray-500 text-xs mt-1 max-w-xs mx-auto">
                {searchQuery ? `No results matching "${searchQuery}". Try searching for another item.` : "No items currently available in this category."}
              </p>
              {(searchQuery || vegOnly || activeCategory !== "all") && (
                <button
                  onClick={() => { setSearchQuery(""); setVegOnly(false); setActiveCategory("all"); }}
                  className="mt-4 px-4 py-1.5 rounded-full bg-[#2A9D8F] text-white text-xs font-semibold hover:bg-[#218377] transition-colors"
                >
                  Reset Filters
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5 md:gap-4">
              {filteredItems.map((item) => {
                const quantity = getItemQuantity(item.id);
                const itemCats = getItemCategories(item);
                let isCatActive, closedCategoryName;
                if (activeCategory === "all") {
                  // In "All" tab: item is orderable if ANY of its categories is currently active
                  const firstActiveCat = [...itemCats].find(cat => {
                    const cs = categorySettings.find(c => c.category === cat);
                    return !cs || (cs.is_enabled !== false && cs.is_active !== false);
                  });
                  isCatActive = !!firstActiveCat;
                  // For the "closed" label, show the primary category
                  closedCategoryName = item.category || [...itemCats][0] || "Item";
                } else {
                  // In a specific tab: check that tab's category setting
                  const catSetting = categorySettings.find(c => c.category === activeCategory);
                  isCatActive = !catSetting || (catSetting.is_enabled !== false && catSetting.is_active !== false);
                  closedCategoryName = activeCategory;
                }
                const isAvailable = item.is_available !== false && isCatActive;
                
                return (
                  <div
                    key={item.id}
                    className={`flex gap-3.5 p-3.5 md:p-4 bg-white rounded-2xl border border-[#E8E4D9]/60 shadow-[0_2px_12px_rgba(0,0,0,0.02)] transition-all hover:shadow-[0_8px_24px_rgba(42,157,143,0.08)] ${
                      !isAvailable ? 'opacity-65' : ''
                    }`}
                    data-testid={`menu-item-${item.id}`}
                  >
                    {/* Item Image */}
                    <div className="relative w-24 h-24 md:w-28 md:h-28 flex-shrink-0 rounded-xl overflow-hidden bg-gray-100">
                      {item.image_url ? (
                        <img
                          src={item.image_url}
                          alt={item.name}
                          className={`w-full h-full object-cover transition-transform duration-300 ${isAvailable ? 'hover:scale-105' : 'grayscale'}`}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = "https://images.unsplash.com/photo-1546069901-ba9599a7e63c?auto=format&fit=crop&q=80&w=400";
                          }}
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-[#2A9D8F]/10 to-[#FFC107]/20 flex items-center justify-center text-gray-400">
                          <ShoppingBag className="w-6 h-6 opacity-40" />
                        </div>
                      )}
                      {!isAvailable && (
                        <div className="absolute inset-0 bg-black/50 flex items-center justify-center p-1">
                          <span className="bg-red-600 text-white font-bold text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full shadow-sm">
                            Sold Out
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 flex flex-col justify-between min-w-0">
                      <div>
                        {/* Title & Dietary Indicator */}
                        <div className="flex items-start gap-1.5">
                          <DietaryBadge isVeg={item.is_vegetarian !== false} />
                          <h3 
                            className="font-bold text-[#264653] text-sm md:text-base leading-tight truncate"
                            style={{ fontFamily: "'Quicksand', sans-serif" }}
                            title={item.name}
                          >
                            {item.name}
                          </h3>
                        </div>
                        
                        {/* Description */}
                        {item.description && (
                          <p className="text-gray-500 text-xs mt-1 line-clamp-2 leading-relaxed">
                            {item.description}
                          </p>
                        )}
                      </div>

                      {/* Bottom Pricing & Add Stepper */}
                      <div className="flex items-center justify-between mt-2.5 pt-1.5 border-t border-gray-100">
                        <span className="font-extrabold text-[#2A9D8F] text-base" style={{ fontFamily: "'Quicksand', sans-serif" }}>
                          ₹{item.price}
                        </span>

                        {isOffline ? (
                          <span className="text-[11px] text-gray-400 font-medium px-2 py-1 bg-gray-100 rounded-lg">Kitchen Closed</span>
                        ) : !isAvailable ? (
                          <span className="text-[11px] text-gray-400 font-medium px-2 py-1 bg-gray-100 rounded-lg">
                            {!isCatActive ? `${(closedCategoryName || "Item").charAt(0).toUpperCase() + (closedCategoryName || "item").slice(1)} Closed` : "Unavailable"}
                          </span>
                        ) : quantity === 0 ? (
                          <Button
                            size="sm"
                            className="h-8 rounded-xl bg-[#2A9D8F] hover:bg-[#218377] text-white px-3.5 text-xs font-bold shadow-sm active:scale-95 transition-all"
                            onClick={() => !isOffline && handleAddToCart(item)}
                            data-testid={`add-to-cart-${item.id}`}
                          >
                            <Plus className="w-3.5 h-3.5 mr-1" />
                            ADD
                          </Button>
                        ) : (
                          <div className="flex items-center gap-1.5 bg-[#2A9D8F]/10 rounded-xl p-1 border border-[#2A9D8F]/20">
                            <button
                              onClick={() => handleUpdateQuantity(item.id, quantity - 1)}
                              className="w-7 h-7 rounded-lg bg-white flex items-center justify-center text-[#2A9D8F] hover:bg-[#2A9D8F] hover:text-white transition-colors shadow-sm active:scale-90"
                              data-testid={`decrease-${item.id}`}
                            >
                              <Minus className="w-3.5 h-3.5" />
                            </button>
                            <span className="w-6 text-center font-bold text-xs text-[#2A9D8F]" data-testid={`quantity-${item.id}`}>
                              {quantity}
                            </span>
                            <button
                              onClick={() => handleUpdateQuantity(item.id, quantity + 1)}
                              className="w-7 h-7 rounded-lg bg-white flex items-center justify-center text-[#2A9D8F] hover:bg-[#2A9D8F] hover:text-white transition-colors shadow-sm active:scale-90"
                              data-testid={`increase-${item.id}`}
                            >
                              <Plus className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Floating Bottom Cart Bar (Appears when items in cart, sits cleanly above bottom dock) */}
        {!isOffline && cartTotalQuantity > 0 && (
          <div className="fixed bottom-24 left-4 right-4 z-40 max-w-md mx-auto animate-fade-in-up">
            <div 
              onClick={() => navigate(`/cart?property=${propertyId}`)}
              className="bg-gradient-to-r from-[#2A9D8F] to-[#48bcae] text-white p-3.5 rounded-2xl shadow-2xl flex items-center justify-between cursor-pointer hover:shadow-teal-900/30 transition-all transform active:scale-95 border border-white/25 backdrop-blur-md"
            >
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center shadow-inner">
                  <ShoppingBag className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-bold text-sm leading-tight">{cartTotalQuantity} {cartTotalQuantity === 1 ? 'item' : 'items'} in order</p>
                  <p className="text-xs text-white/85 font-medium">₹{cartTotalPrice} total</p>
                </div>
              </div>
              <div className="flex items-center gap-1 font-bold text-xs bg-white/20 hover:bg-white/30 px-3.5 py-2 rounded-xl transition-colors">
                <span>VIEW CART</span>
                <ChevronRight className="w-4 h-4" />
              </div>
            </div>
          </div>
        )}
      </div>
    </GuestLayout>
  );
}
